// Modal de edición financiera de UN envío, compartido por Liquidaciones >
// Clientes y Liquidaciones > Choferes (ver informe "Liquidaciones ->
// Choferes" — "reutilizar exactamente el mismo componente si es posible").
// Turbo, ×2 Doble, Precio manual y Código postal, presentados como
// acciones visuales. Nunca calcula nada por su cuenta: solo manda el
// override al endpoint indicado por el llamador (overrideUrl), que decide
// el precio final (misma precedencia siempre: manual > ×2 Doble > Turbo >
// CP/tier). El llamador (ClienteDetailModal o DriverDetailModal) arma la
// URL del override — Store y Driver son endpoints distintos, la lógica de
// edición es la misma.
import React, { useEffect, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { authFetch } from "../../lib/authFetch";

// Superset de campos: Store y Driver comparten casi todo (ver
// toDeliveryDetailRow / toDriverDeliveryDetailRow en el backend) — los que
// son propios de un solo lado quedan opcionales.
export type DeliveryDetail = {
  lightdata_id: number | string;
  tracking: string | null;
  fecha_colecta: string | null;
  destinatario: string | null;
  direccion: string | null;
  original_direccion?: string | null; // legacy.envios.direccion tal cual, nunca se toca
  has_direccion_override?: boolean;
  original_cp: string | null; // legacy.envios.cp tal cual, nunca se toca
  cp: string | null; // effectivePostalCode: el que realmente se usa (con corrección si existe)
  has_postal_code_override: boolean;
  tier: string | null;
  has_tier_override?: boolean;
  tariff_name: string | null;
  estado: string | null; // estado OPERATIVO real (legacy.envios.estado)
  is_turbo: boolean;
  turbo_source: "REAL" | "MANUAL" | null;
  turbo_price: number | null; // preview: precio Turbo de la tarifa, exista o no isTurbo activo
  is_double: boolean;
  calculated_base_price?: number | null;
  calculated_price: number | null;
  discount?: number | null; // solo Store — Driver no tiene descuento
  manual_price: number | null;
  final_price: number | null;
  pricing_status: string;
  pricing_reason: string | null;
  has_override: boolean;
  override_observation: string | null;
  store_name?: string | null; // solo Driver: a qué Store pertenece el envío (informativo)
  lightdata_username?: string | null; // solo Driver
  // Premio Elite (solo Driver, ver informe "LYNX — Driver Daily Performance
  // + Premios Elite"): base_price es el comercial SIN premio (equivalente a
  // calculated_price); final_price acá SÍ incluye elite_bonus. Congelado por
  // día — nunca se recalcula una vez que el día cerró.
  base_price?: number | null;
  elite_eligible?: boolean;
  elite_awarded?: boolean;
  elite_bonus?: number | null;
  elite_status?: "AWARDED" | "LOST" | "NOT_ELIGIBLE" | "UNPRICED" | "TURBO" | null;
  elite_reason?: string | null;
  // Cobranza (ver informe "Nueva respuesta LightData"): SOLO Store — dinero
  // del Seller cobrado en la entrega, nunca un concepto de Driver. Su
  // presencia (undefined en Driver) es lo que decide si el modal muestra la
  // opción, sin necesidad de un prop de "tipo de entidad" nuevo.
  lightdata_cobranza?: number | null;
  cobranza?: number | null;
  has_cobranza_override?: boolean;
};

export function money(n: number | null): string {
  if (n === null || n === undefined) return "—";
  return `$${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const optionCardStyle = (active: boolean): React.CSSProperties => ({
  flex: "1 1 140px",
  minWidth: "140px",
  padding: "14px",
  borderRadius: "16px",
  border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
  background: active ? "var(--primary-soft)" : "transparent",
  cursor: "pointer",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  alignItems: "center",
  transition: "all var(--transition-base, 0.15s)",
});

export function OptionCard({
  icon,
  label,
  sublabel,
  active,
  disabled,
  onClick,
}: {
  icon: string;
  label: string;
  sublabel?: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{ ...optionCardStyle(active), opacity: disabled ? 0.45 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
    >
      <span style={{ fontSize: "22px" }}>{icon}</span>
      <span style={{ fontSize: "13px", fontWeight: 700, color: "var(--text-primary)" }}>{label}</span>
      {sublabel && <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{sublabel}</span>}
    </button>
  );
}

const overlayStyle: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 140, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" };
const backdropStyle: React.CSSProperties = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)" };

const VALID_TIERS = ["CABA", "GBA1", "GBA2", "GBA3"] as const;
type TierValue = (typeof VALID_TIERS)[number];

export type ReportType = "TURBO" | "PRECIO_MANUAL" | "DOBLE" | "DIRECCION_ESPECIAL" | "OTRO";

export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  TURBO: "Turbo",
  PRECIO_MANUAL: "Precio manual",
  DOBLE: "Doble",
  DIRECCION_ESPECIAL: "Dirección especial",
  OTRO: "Otro",
};

// Target para mode="report": arma el body que espera POST /v1/reports
// (lightdata_id + exactamente uno de store_id/driver_id).
export type ReportTarget = {
  lightdataId: number | string;
  storeId?: number | string;
  driverId?: number | string;
};

export default function OverrideEditModal({
  open,
  mode = "apply",
  entityLabel,
  token,
  delivery,
  overrideUrl,
  reportTarget,
  approveUrl,
  onClose,
  onSaved,
}: {
  open: boolean;
  // "apply" (Liquidaciones, default): aplica el cambio de inmediato vía
  // overrideUrl. "report" (Coordinador): genera un Reporte Operativo
  // pendiente vía POST /v1/reports — NUNCA toca el override real. "approve"
  // (Liquidaciones, "Editar" desde el detalle de un reporte): ajusta
  // Turbo/Doble/Manual de un reporte YA creado y lo aprueba en el mismo
  // paso, vía PUT approveUrl. Mismo componente, tres comportamientos (ver
  // informe "Sprint Operativo").
  mode?: "apply" | "report" | "approve";
  entityLabel: string | null; // "Store: X" o "Chofer: X", armado por el llamador
  token: string | null;
  delivery: DeliveryDetail | null;
  overrideUrl?: string; // requerido en mode="apply": PUT completo (Store o Driver)
  reportTarget?: ReportTarget; // requerido en mode="report"
  approveUrl?: string; // requerido en mode="approve": PUT /v1/reports/:id/approve
  onClose: () => void;
  onSaved: (detail: any) => void;
}) {
  const [turboOn, setTurboOn] = useState(false);
  const [doubleOn, setDoubleOn] = useState(false);
  const [manualOn, setManualOn] = useState(false);
  const [manualPrice, setManualPrice] = useState("");
  const [cpOn, setCpOn] = useState(false);
  const [cpValue, setCpValue] = useState("");
  const [cpLookup, setCpLookup] = useState<{ status: "idle" | "loading" | "found" | "not_found"; tier?: string; price?: number }>({ status: "idle" });
  const [direccionOn, setDireccionOn] = useState(false);
  const [direccionValue, setDireccionValue] = useState("");
  const [tierOn, setTierOn] = useState(false);
  const [tierValue, setTierValue] = useState<TierValue | "">("");
  const [cobranzaOn, setCobranzaOn] = useState(false);
  const [cobranzaValue, setCobranzaValue] = useState("");
  const [reportType, setReportType] = useState<ReportType | "">("");
  const [observation, setObservation] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isReportMode = mode === "report";
  const isApproveMode = mode === "approve";
  // CP nunca aplica a un Reporte Operativo (ni al crearlo ni al aprobarlo
  // con cambios) — no es una decisión operativa de Coordinador, ver Parte 6
  // del informe original.
  const hideCpOption = isReportMode || isApproveMode;
  // report_mode siempre arranca en blanco (es una solicitud nueva);
  // approve_mode SÍ precarga (está editando lo que el Coordinador ya pidió).
  const shouldPrefill = !isReportMode;
  // Cobranza: SOLO Store (ver DeliveryDetail) y SOLO mode="apply" — no es
  // una decisión operativa de Coordinador ni algo que se "apruebe" desde un
  // Reporte Operativo (ver informe "Nueva respuesta LightData").
  const hasCobranzaConcept = delivery?.cobranza !== undefined;
  const showCobranzaOption = mode === "apply" && hasCobranzaConcept;

  useEffect(() => {
    if (!delivery) return;
    // mode="report" arranca siempre en blanco: es una solicitud nueva, no
    // hay "estado actual" que precargar. mode="apply" y mode="approve" SÍ
    // precargan (el override vigente, o lo que pidió el Coordinador).
    setTurboOn(shouldPrefill && delivery.turbo_source === "MANUAL");
    setDoubleOn(shouldPrefill && delivery.is_double);
    const hasManual = shouldPrefill && delivery.manual_price !== null;
    setManualOn(hasManual);
    setManualPrice(hasManual ? String(delivery.manual_price) : "");
    setCpOn(!hideCpOption && delivery.has_postal_code_override);
    setCpValue(!hideCpOption && delivery.has_postal_code_override ? delivery.cp || "" : "");
    setCpLookup({ status: "idle" });
    setDireccionOn(!hideCpOption && delivery.has_direccion_override === true);
    setDireccionValue(!hideCpOption && delivery.has_direccion_override ? delivery.direccion || "" : "");
    setTierOn(!hideCpOption && delivery.has_tier_override === true);
    setTierValue(!hideCpOption && delivery.has_tier_override && delivery.tier ? (delivery.tier as TierValue) : "");
    setCobranzaOn(showCobranzaOption && delivery.has_cobranza_override === true);
    setCobranzaValue(showCobranzaOption && delivery.has_cobranza_override ? String(delivery.cobranza ?? "") : "");
    setReportType("");
    setObservation(shouldPrefill ? delivery.override_observation || "" : "");
    setError(null);
  }, [delivery, shouldPrefill, hideCpOption, showCobranzaOption]);

  // Validación de CP: consulta el lookup YA existente (/v2/tariffs/lookup/:code,
  // reutilizado — no se crea un endpoint nuevo) y busca si la tarifa de
  // esta entidad tiene ese CP configurado. NO se infiere CABA/GBA por rango.
  useEffect(() => {
    if (!cpOn || !delivery) {
      setCpLookup({ status: "idle" });
      return;
    }
    const code = cpValue.trim();
    if (!/^\d{4}$/.test(code)) {
      setCpLookup({ status: "idle" });
      return;
    }
    let cancelled = false;
    setCpLookup({ status: "loading" });
    const timer = setTimeout(() => {
      authFetch(`/v2/tariffs/lookup/${code}`, { token })
        .then(async (res) => {
          const payload = await res.json().catch(() => ({}));
          if (cancelled) return;
          const match = Array.isArray(payload?.results) ? payload.results.find((r: any) => r.name === delivery.tariff_name) : null;
          if (match) setCpLookup({ status: "found", tier: match.tier, price: Number(match.price) });
          else setCpLookup({ status: "not_found" });
        })
        .catch(() => {
          if (!cancelled) setCpLookup({ status: "not_found" });
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cpOn, cpValue, delivery, token]);

  if (!open || !delivery) return null;

  // Preview en vivo: misma precedencia que el backend (manual > ×2 > turbo > CP > calculado).
  const cpBase = cpOn && cpLookup.status === "found" ? cpLookup.price ?? null : null;
  const autoBase = turboOn ? delivery.turbo_price : cpOn ? cpBase : delivery.calculated_price;
  const autoPreview = autoBase !== null ? (doubleOn ? autoBase * 2 : autoBase) : null;
  const manualValue = manualOn && manualPrice.trim() !== "" && !Number.isNaN(Number(manualPrice)) ? Number(manualPrice) : null;
  const finalPreview = manualValue !== null ? manualValue : autoPreview;

  const submit = async (clear = false) => {
    if (isReportMode && !reportType) {
      setError("Elegí un motivo para el reporte");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let res: Response;
      if (isReportMode) {
        res = await authFetch("/v1/reports", {
          method: "POST",
          token,
          json: true,
          body: JSON.stringify({
            lightdata_id: reportTarget?.lightdataId,
            store_id: reportTarget?.storeId ?? null,
            driver_id: reportTarget?.driverId ?? null,
            manual_price: manualOn && manualPrice.trim() !== "" ? Number(manualPrice) : null,
            force_turbo: turboOn,
            double_price: doubleOn,
            report_type: reportType,
            observation: observation.trim() || null,
          }),
        });
      } else if (isApproveMode) {
        res = await authFetch(approveUrl as string, {
          method: "PUT",
          token,
          json: true,
          body: JSON.stringify({
            manual_price: manualOn && manualPrice.trim() !== "" ? Number(manualPrice) : null,
            force_turbo: turboOn,
            double_price: doubleOn,
          }),
        });
      } else {
        const body = clear
          ? {
              manual_price: null,
              force_turbo: false,
              double_price: false,
              postal_code_override: null,
              direccion_override: null,
              tier_override: null,
              cobranza_override: null,
              observation: null,
            }
          : {
              manual_price: manualOn && manualPrice.trim() !== "" ? Number(manualPrice) : null,
              force_turbo: turboOn,
              double_price: doubleOn,
              postal_code_override: cpOn && cpValue.trim() !== "" ? cpValue.trim() : null,
              direccion_override: direccionOn && direccionValue.trim() !== "" ? direccionValue.trim() : null,
              tier_override: tierOn && tierValue ? tierValue : null,
              cobranza_override: cobranzaOn && cobranzaValue.trim() !== "" ? Number(cobranzaValue) : null,
              observation: observation.trim() || null,
            };
        res = await authFetch(overrideUrl as string, {
          method: "PUT",
          token,
          json: true,
          body: JSON.stringify(body),
        });
      }
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "No se pudo guardar el cambio");
      onSaved(payload);
    } catch (e: any) {
      setError(e.message || "No se pudo guardar el cambio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={backdropStyle} onClick={() => !saving && onClose()} />
      <div style={{ position: "relative", width: "100%", maxWidth: "520px" }}>
        <Card padding="20px" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "var(--text-primary)" }}>
              {isReportMode ? "Reportar envío" : isApproveMode ? "Editar y aprobar reporte" : "Editar envío"} {delivery.tracking || delivery.lightdata_id}
            </h3>
            {isReportMode && (
              <p style={{ margin: "4px 0 0", fontSize: "12.5px", color: "var(--text-secondary)" }}>
                Esto NO modifica el precio — queda pendiente hasta que Liquidaciones lo apruebe.
              </p>
            )}
            {isApproveMode && (
              <p style={{ margin: "4px 0 0", fontSize: "12.5px", color: "var(--text-secondary)" }}>
                Ajustá lo que pidió el Coordinador antes de aprobar — al guardar, se aplica de inmediato.
              </p>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px", fontSize: "13px" }}>
            <div><span style={{ color: "var(--text-secondary)" }}>{entityLabel || "—"}</span></div>
            {delivery.has_postal_code_override ? (
              <div>
                <span style={{ color: "var(--text-secondary)" }}>CP informado por LightData: </span>{delivery.original_cp || "Sin informar"}
                <br />
                <span style={{ color: "var(--text-secondary)" }}>CP utilizado para liquidación: </span>{delivery.cp || "—"}
              </div>
            ) : (
              <div><span style={{ color: "var(--text-secondary)" }}>CP: </span>{delivery.cp || "Sin informar"}</div>
            )}
            {delivery.has_direccion_override ? (
              <div style={{ gridColumn: "1 / -1" }}>
                <span style={{ color: "var(--text-secondary)" }}>Dirección informada por LightData: </span>{delivery.original_direccion || "Sin informar"}
                <br />
                <span style={{ color: "var(--text-secondary)" }}>Dirección utilizada para liquidación: </span>{delivery.direccion || "—"}
              </div>
            ) : (
              <div style={{ gridColumn: "1 / -1" }}><span style={{ color: "var(--text-secondary)" }}>Dirección: </span>{delivery.direccion || "Sin informar"}</div>
            )}
            <div style={{ gridColumn: "1 / -1" }}><span style={{ color: "var(--text-secondary)" }}>Precio actual: </span><strong>{money(delivery.final_price)}</strong></div>
          </div>

          <div>
            <p style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: 700, color: "var(--text-primary)" }}>¿Qué querés aplicar?</p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <OptionCard
                icon="⚡"
                label="Turbo"
                sublabel={mode === "apply" ? (delivery.turbo_price !== null ? money(delivery.turbo_price) : "No configurado") : undefined}
                active={turboOn}
                // En mode="report"/"approve" no hay un preview de precio
                // cargado (no se resuelve tarifa para armar el pedido) —
                // deshabilitar acá bloquearía marcar "Turbo" como motivo,
                // que es justamente el caso de uso.
                disabled={mode === "apply" && delivery.turbo_price === null}
                onClick={() => setTurboOn((v) => !v)}
              />
              <OptionCard
                icon="×2"
                label="Doble"
                sublabel={autoBase !== null ? money(autoBase * 2) : "—"}
                active={doubleOn}
                onClick={() => setDoubleOn((v) => !v)}
              />
              <OptionCard
                icon="✏️"
                label="Precio manual"
                active={manualOn}
                onClick={() => setManualOn((v) => !v)}
              />
              {!hideCpOption && (
                <OptionCard
                  icon="📍"
                  label="Código postal"
                  active={cpOn}
                  onClick={() => setCpOn((v) => !v)}
                />
              )}
              {!hideCpOption && (
                <OptionCard
                  icon="🏠"
                  label="Dirección"
                  active={direccionOn}
                  onClick={() => setDireccionOn((v) => !v)}
                />
              )}
              {!hideCpOption && (
                <OptionCard
                  icon="🗺️"
                  label="Zona"
                  active={tierOn}
                  onClick={() => setTierOn((v) => !v)}
                />
              )}
              {showCobranzaOption && (
                <OptionCard
                  icon="💰"
                  label="Cobranza"
                  sublabel={delivery.lightdata_cobranza !== null && delivery.lightdata_cobranza !== undefined ? `LightData: ${money(delivery.lightdata_cobranza)}` : "Sin informar"}
                  active={cobranzaOn}
                  onClick={() => setCobranzaOn((v) => !v)}
                />
              )}
            </div>
          </div>

          {isReportMode && (
            <div>
              <p style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: 700, color: "var(--text-primary)" }}>Motivo del reporte</p>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {(Object.keys(REPORT_TYPE_LABELS) as ReportType[]).map((rt) => (
                  <OptionCard
                    key={rt}
                    icon="📝"
                    label={REPORT_TYPE_LABELS[rt]}
                    active={reportType === rt}
                    onClick={() => setReportType(rt)}
                  />
                ))}
              </div>
            </div>
          )}

          {manualOn && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                Precio calculado: {money(delivery.calculated_price)}
              </div>
              <Input
                label="Precio final"
                type="number"
                step="0.01"
                placeholder="$"
                value={manualPrice}
                onChange={(e) => setManualPrice(e.target.value)}
                autoFocus
              />
            </div>
          )}

          {cpOn && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                CP informado por LightData: {delivery.original_cp || "Sin informar"}
              </div>
              <Input
                label="Código postal para liquidación"
                placeholder="Ej: 1842"
                value={cpValue}
                onChange={(e) => setCpValue(e.target.value.replace(/\D/g, "").slice(0, 4))}
                autoFocus
              />
              {cpLookup.status === "loading" && <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>Consultando…</div>}
              {cpLookup.status === "found" && (
                <div style={{ fontSize: "12px", color: "var(--success, #2ea043)" }}>
                  → {cpLookup.tier} · {money(cpLookup.price ?? null)}
                </div>
              )}
              {cpLookup.status === "not_found" && (
                <div style={{ fontSize: "12px", color: "var(--danger, #d1242f)" }}>
                  El código postal {cpValue} no está configurado en esta tarifa.
                </div>
              )}
            </div>
          )}

          {direccionOn && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                Dirección informada por LightData: {delivery.original_direccion || delivery.direccion || "Sin informar"}
              </div>
              <Input
                label="Dirección para liquidación"
                placeholder="Ej: Av. Siempre Viva 742"
                value={direccionValue}
                onChange={(e) => setDireccionValue(e.target.value)}
                autoFocus
              />
            </div>
          )}

          {tierOn && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Zona para liquidación</div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {VALID_TIERS.map((t) => (
                  <OptionCard key={t} icon="🗺️" label={t} active={tierValue === t} onClick={() => setTierValue(t)} />
                ))}
              </div>
            </div>
          )}

          {cobranzaOn && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                Cobranza informada por LightData: {money(delivery.lightdata_cobranza ?? null)}. Nunca modifica el precio del envío — es dinero del Seller que se descuenta aparte, en la liquidación.
              </div>
              <Input
                label="Cobranza para liquidación"
                type="number"
                step="0.01"
                placeholder="$"
                value={cobranzaValue}
                onChange={(e) => setCobranzaValue(e.target.value)}
                autoFocus
              />
            </div>
          )}

          <div style={{ padding: "10px 14px", borderRadius: "12px", background: "var(--primary-soft)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>{isReportMode ? "Precio propuesto" : "Precio final estimado"}</span>
            <strong style={{ fontSize: "16px", color: "var(--text-primary)" }}>{money(finalPreview)}</strong>
          </div>

          {isApproveMode ? (
            observation && (
              <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>Observación del Coordinador: </span>{observation}
              </div>
            )
          ) : (
            <Input
              label="Observación (opcional)"
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              placeholder="Ej: Saglietti combo, direcciones repetidas..."
            />
          )}

          {error && <div style={{ color: "var(--danger, #d1242f)", fontSize: "13px" }}>{error}</div>}

          <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
            {mode === "apply" && delivery.has_override ? (
              <Button variant="danger" onClick={() => submit(true)} disabled={saving}>
                Quitar cambios
              </Button>
            ) : (
              <span />
            )}
            <div style={{ display: "flex", gap: "8px" }}>
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={() => submit(false)} disabled={saving}>
                {saving ? "Guardando…" : isReportMode ? "Enviar reporte" : isApproveMode ? "Aprobar con cambios" : "Guardar"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
